import PropTypes from 'prop-types';
import React from 'react';
import './ActionBar.css';
import MuiIcons from '../utils/MuiIcons';
import ToolTip from '../../../../components/ToolTipIcon/ToolTip';

export class ActionButton {
  /**
   * @type {number}
   */
  static #instanceCounter = 0;

  /**
   * @returns {number} A unique ID for the Button instance.
   */
  static #incrementAndReturnCounter() {
    ActionButton.#instanceCounter += 1;

    return ActionButton.#instanceCounter;
  }

  /**
   * Private data storage
   */
  #data;

  /**
   * @param {string} title - The display title of the button
   * @param {string} icon - The MUI icon text of the button
   * @param {(() => void) | null} onClick - Optional click handler function
   * @param {Object} options - Optional configuration object for the button
   * @param {boolean} [options.disabled=false] - indicates if the button is disabled
   * @param {string} [options.extraClasses=''] - additional CSS classes for the button
   * @param {string} [options.tooltip=''] - tooltip text for the button
   * @param {string} [options.dataTest=null] - optional unique key for the button
   */
  constructor(title, icon, onClick, options = {}) {
    if (onClick != null && typeof onClick !== 'function') {
      throw new TypeError('Button onClick must be null, or a function');
    }

    this.#data = {
      title: String(title),
      icon: String(icon),
      onClick,
      disabled: Boolean(options?.disabled) || false,
      extraClasses: String(options?.extraClasses || ''),
      tooltip: String(options?.tooltip || ''),
      dataTest: options?.dataTest ? String(options.dataTest) : null,
      key: ActionButton.#incrementAndReturnCounter(),
    };
  }

  /**
   * Gets the title of the button.
   * @returns {string} The title of the button.
   */
  get title() {
    return this.#data.title;
  }

  /**
   * Gets the click handler function of the button.
   * @returns {(() => void) | null} The click handler function.
   */
  get onClick() {
    return this.#data.onClick;
  }

  /**
   * Gets the MUI icon text of the button.
   * @returns {string} The icon of the button.
   */
  get icon() {
    return this.#data.icon;
  }

  /**
   * Gets the disabled state of the button.
   * @returns {boolean} The disabled state of the button.
   */
  get disabled() {
    return this.#data.disabled;
  }

  /**
   * Gets the unique key for this button instance.
   * @returns {number} The unique key for this button instance.
   */
  get key() {
    return this.#data.key;
  }

  /**
   * Gets the additional CSS classes for the button.
   * @returns {string} The additional CSS classes for the button.
   */
  get extraClasses() {
    return this.#data.extraClasses;
  }

  /**
   * Gets the tooltip text for the button.
   * @returns {string} The tooltip text for the button.
   */
  get tooltip() {
    return this.#data.tooltip;
  }

  /**
   * Gets the data-test attribute value for the button.
   * @returns {string|null} The data-test attribute value for the button, or null if not set.
   */
  get dataTest() {
    return this.#data.dataTest;
  }

  /**
   * Gets the CSS class for the button's icon.
   */
  get styleClass() {
    return MuiIcons.className;
  }
}

export class ActionButtonImg extends ActionButton {
  /**
   * Gets the MUI icon text of the button.
   * @returns {string} The icon of the button.
   */
  get icon() {
    return <img src={super.icon} alt={super.title} draggable={false} />;
  }

  /**
   * Gets the CSS class for the button's icon.
   */
  get styleClass() {
    return MuiIcons.notMuiClassName;
  }
}

/**
 * A React component that renders an action bar with buttons.
 * @param {Object} props - The component props.
 * @param {ActionButton[]} props.buttons - An array of ActionButton instances containing button configurations.
 * @returns {JSX.Element}
 */
function ActionBar({ buttons }) {
  /**
   * Renders a button element based on the provided ActionButton instance.
   * @param {ActionButton} button - The button to render.
   * @returns {JSX.Element}
   */
  const renderButton = button => {
    return (
      <ToolTip toolText={button.tooltip || button.title} key={button.key}>
        <button
          data-test={button.dataTest || button.key}
          type="button"
          className={`action-body ${button.disabled ? 'disabled' : ''} ${button.extraClasses}`}
          onClick={button.disabled ? null : button.onClick}
          disabled={button.disabled}
        >
          <div className={`action-image ${button.styleClass}`}>{button.icon}</div>
          <div className="action-text">{button.title}</div>
        </button>
      </ToolTip>
    );
  };

  return React.useMemo(() => {
    return <div className="wizard-action-bar">{buttons.map(button => renderButton(button))}</div>;
  }, [buttons]);
}

const propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.instanceOf(ActionButton)).isRequired,
};

ActionBar.propTypes = propTypes;

export default ActionBar;
